import {
  EnemyClassificationIcon,
  FriendClassificationIcon,
  SuspectClassificationIcon,
  UnclassifiedClassificationIcon,
  UnknownClassificationIcon,
  WatchClassificationIcon,
} from '@assets/icons/24x24/target-ic-statusColor';
import {
  AircraftSignIcon,
  BirdSignIcon,
  DetectorSignIcon,
  DroneSignIcon,
  GroundVehicleSignIcon,
  HelicopterSignIcon,
  PersonSignIcon,
  RemoteControlSignIcon,
  ShipSignIcon,
  UnknownSignIcon,
  VcaSignIcon,
} from '@assets/icons/24x24/target-icSign';
import { appIntl } from '@components/locale/IntlGlobalProvider';
import { extendedSensorShortHand } from '@utils/map/map.constants';
import { sensorFamilyFromSensorType } from '@utils/sensors/configuration.constants';
import { FunctionComponent, SVGProps } from 'react';

import { TacticalClearanceEnum, Target, TargetStatusEnum, Track } from '@/types/c2/c2.types';
import { Classification, ClassificationEnum } from '@/types/sensor/classification.types';
import { IdentificationEnum, ReliabilityEnum } from '@/types/sensor/identification.types';
import {
  AbstractLocation,
  LocationTypeEnum,
  PlanarLocation,
  SpatialLocation,
  TargetLocation,
  TendencyEnum,
  TrackerLocation,
} from '@/types/sensor/sensor.types';
import { TrackLight } from '@/types/sensor/sensors';
import {
  complexTargetIcons,
  DataInfoSizeEnum,
  DataInfoStatusEnum,
  dataStatusInfoIcons,
  IconTypeEnum,
  simpleTargetIcons,
  tacticalClearanceDataStatus,
  targetIconColorFromClassification,
  targetIconIdentification,
} from '@/types/target.types';

import { isOrientedTarget } from './map/map.utils';

export function isIdentificationEnum(value: unknown): value is IdentificationEnum {
  return typeof value === 'string' && Object.keys(IdentificationEnum).includes(value);
}

export function isClassificationEnum(value: unknown): value is ClassificationEnum {
  return typeof value === 'string' && Object.keys(ClassificationEnum).includes(value);
}

export function resolveIdentification(
  value: IdentificationEnum | Partial<Record<string, IdentificationEnum>> | null | undefined,
  organization: string | null,
): IdentificationEnum | undefined {
  if (isIdentificationEnum(value)) {
    return value;
  }

  return value && organization && Object.hasOwn(value, organization) ? value[organization] : undefined;
}

export function resolveClassification(
  value: ClassificationEnum | Partial<Record<string, ClassificationEnum>> | null | undefined,
  organization: string | null,
): ClassificationEnum | undefined {
  if (isClassificationEnum(value)) {
    return value;
  }

  return value && organization && Object.hasOwn(value, organization) ? value[organization] : undefined;
}

export function getTargetIdentification(
  target: Target<AbstractLocation>,
  organization: string | null,
): IdentificationEnum {
  const { manualIdentification, autoIdentification } = target.identification;

  return resolveIdentification(manualIdentification, organization) ?? autoIdentification;
}

export function getTargetClassification<T extends { classification: Classification }>(
  { classification }: T,
  organization: string | null,
): ClassificationEnum {
  const { manualClassification, classification: autoClassification } = classification;

  if (isClassificationEnum(manualClassification)) {
    return manualClassification;
  }
  if (isClassificationEnum(autoClassification)) {
    return autoClassification;
  }

  return (
    resolveClassification(manualClassification, organization) ??
    resolveClassification(autoClassification, organization) ??
    ClassificationEnum.UNKNOWN
  );
}

export function getTargetReliability(target: Target<AbstractLocation>): ReliabilityEnum {
  return target.reliability;
}

export function getTargetStatus(target: Target<AbstractLocation>): TargetStatusEnum {
  return target.status;
}

export function isDrone(target: Target<AbstractLocation>, organization: string | null): boolean {
  return getTargetIdentification(target, organization) === IdentificationEnum.DRONE;
}

export function isRemoteControl(target: Target<AbstractLocation>, organization: string | null): boolean {
  return getTargetIdentification(target, organization) === IdentificationEnum.REMOTE_CONTROL;
}

export function isAircraft(target: Target<AbstractLocation>, organization: string | null): boolean {
  return getTargetIdentification(target, organization) === IdentificationEnum.AIRCRAFT;
}

export function isVCA(target: Target<AbstractLocation>, organization: string | null): boolean {
  return getTargetIdentification(target, organization) === IdentificationEnum.VCA;
}

export function isSure(target: Target<AbstractLocation>): boolean {
  return getTargetReliability(target) === ReliabilityEnum.SURE;
}

export function isSpatial(target: Target<AbstractLocation>): target is Target<SpatialLocation> {
  return target.lastPosition?.location?.type === LocationTypeEnum.SPATIAL;
}

export function isPlanar(target: Target<AbstractLocation>): target is Target<PlanarLocation> {
  return target.lastPosition?.location?.type === LocationTypeEnum.PLANAR;
}

export function isTracker(track: Track<AbstractLocation>): track is Track<TrackerLocation> {
  return track.lastPosition?.location?.type === LocationTypeEnum.TRACKER;
}

export function getTargetAltitude(target: Target<AbstractLocation>): number | null {
  if (isSpatial(target)) {
    return target.lastPosition.location?.altitude ?? null;
  }
  return null;
}

export function getTargetHeight(target: Target<AbstractLocation>): number | null {
  if (isSpatial(target)) {
    return target.lastPosition.location?.height ?? null;
  }
  return null;
}

export function getTargetTendency(target: Target<AbstractLocation>): TendencyEnum | null {
  if (isSpatial(target)) {
    return target.lastPosition.location?.tendency ?? null;
  }
  return null;
}

export function getTargetSpeed(target: Target<AbstractLocation>): number | null {
  if (isPlanar(target) || isSpatial(target)) {
    return target.lastPosition.location?.speed ?? null;
  }
  return null;
}

export function getTargetPriority(target: Target<AbstractLocation>, organization: string | null): number {
  switch (getTargetClassification(target, organization)) {
    case ClassificationEnum.FRIEND:
      return 0;
    case ClassificationEnum.UNCLASSIFIED:
      return 1;
    case ClassificationEnum.UNKNOWN:
      return 2;
    case ClassificationEnum.WATCH:
      return 3;
    case ClassificationEnum.SUSPECT:
      return 4;
    case ClassificationEnum.ENEMY:
      return 5;
  }
}

function getClassification(target: Target<TargetLocation>, organization: string | null): ClassificationEnum {
  let classification;

  if (isDrone(target, organization) || isRemoteControl(target, organization)) {
    classification = getTargetClassification(target, organization);
    //We don't want an unclassified drone or remote, so we replace it with unknown instead
    if (classification === ClassificationEnum.UNCLASSIFIED) {
      classification = ClassificationEnum.UNKNOWN;
    }
  } else {
    classification = ClassificationEnum.UNCLASSIFIED;
  }
  return classification;
}

export function getTargetIconName(target: Target<TargetLocation>, organization: string | null): string {
  const identification = getTargetIdentification(target, organization);
  const reliability = getTargetReliability(target);
  const classification = getClassification(target, organization);

  const color = targetIconColorFromClassification[classification];
  const iconReliability = reliability === ReliabilityEnum.UNSURE ? 'unsure' : 'sure';
  const status = target.status === TargetStatusEnum.LOST ? '-lost' : '';

  return `icSymb-${targetIconIdentification[identification]}-${color}-L-${iconReliability}${status}`;
}

export function getContingencyIcon(target: Target<TargetLocation>, organization: string | null): string {
  const color = targetIconColorFromClassification[getClassification(target, organization)];

  return `icSymb-diamond-warning-${color}`;
}

export function getTargetRotation(target: Target<TargetLocation>, organization: string | null): number {
  if (isOrientedTarget(target, organization) && isSure(target)) {
    return target.lastPosition.location?.heading ?? 0;
  }
  return 0;
}

export function getExpiredDroneIconName(classification: ClassificationEnum, reliability: ReliabilityEnum): string {
  //We don't want an unclassified drone, so we replace it with unknown instead
  if (classification === ClassificationEnum.UNCLASSIFIED) {
    classification = ClassificationEnum.UNKNOWN;
  }
  const color = targetIconColorFromClassification[classification];
  const iconReliability = reliability === ReliabilityEnum.UNSURE ? 'unsure' : 'sure';

  return `icSymb-droneGhost-${color}-L-${iconReliability}`;
}

export function getTargetIcon(
  target: Target<TargetLocation>,
  isEngaged = false,
  organization: string | null,
): FunctionComponent<SVGProps<SVGSVGElement>> {
  const identification = getTargetIdentification(target, organization);
  if (identification !== IdentificationEnum.DRONE && identification !== IdentificationEnum.REMOTE_CONTROL) {
    return simpleTargetIcons[identification][getTargetReliability(target)][
      isEngaged ? IconTypeEnum.NEUTRAL : IconTypeEnum.NORMAL
    ];
  } else {
    return complexTargetIcons[identification][getTargetClassification(target, organization)][
      getTargetReliability(target)
    ][isEngaged ? IconTypeEnum.NEUTRAL : IconTypeEnum.NORMAL];
  }
}

export function getIdentificationIcon(identification: IdentificationEnum): FunctionComponent<SVGProps<SVGSVGElement>> {
  switch (identification) {
    case IdentificationEnum.AIRCRAFT:
      return AircraftSignIcon;
    case IdentificationEnum.VCA:
      return VcaSignIcon;
    case IdentificationEnum.BIRD:
      return BirdSignIcon;
    case IdentificationEnum.BUILDING:
      return DetectorSignIcon;
    case IdentificationEnum.GROUND_VEHICLE:
      return GroundVehicleSignIcon;
    case IdentificationEnum.HELICOPTER:
      return HelicopterSignIcon;
    case IdentificationEnum.PERSON:
      return PersonSignIcon;
    case IdentificationEnum.SHIP:
      return ShipSignIcon;
    case IdentificationEnum.UNKNOWN:
      return UnknownSignIcon;
    case IdentificationEnum.DRONE:
      return DroneSignIcon;
    case IdentificationEnum.REMOTE_CONTROL:
      return RemoteControlSignIcon;
  }
}

export function getClassificationIcon(classification: ClassificationEnum): FunctionComponent<SVGProps<SVGSVGElement>> {
  switch (classification) {
    case ClassificationEnum.ENEMY:
      return EnemyClassificationIcon;
    case ClassificationEnum.SUSPECT:
      return SuspectClassificationIcon;
    case ClassificationEnum.WATCH:
      return WatchClassificationIcon;
    case ClassificationEnum.FRIEND:
      return FriendClassificationIcon;
    case ClassificationEnum.UNCLASSIFIED:
      return UnclassifiedClassificationIcon;
    case ClassificationEnum.UNKNOWN:
    default:
      return UnknownClassificationIcon;
  }
}

export function getDataStatusInfoIcon(
  dataStatus: DataInfoStatusEnum,
  size: DataInfoSizeEnum,
): FunctionComponent<SVGProps<SVGSVGElement>> {
  return dataStatusInfoIcons[dataStatus][size];
}

export function getReliabilityStatusIcon(
  reliability: ReliabilityEnum,
  size: DataInfoSizeEnum,
): FunctionComponent<SVGProps<SVGSVGElement>> {
  const dataStatus = reliability === ReliabilityEnum.SURE ? DataInfoStatusEnum.OK : DataInfoStatusEnum.MEDIUM;
  return getDataStatusInfoIcon(dataStatus, size);
}

export function getTacticalClearanceStatusIcon(
  tacticalClearance: TacticalClearanceEnum,
  size: DataInfoSizeEnum,
): FunctionComponent<SVGProps<SVGSVGElement>> {
  const dataStatus = tacticalClearanceDataStatus[tacticalClearance];
  return getDataStatusInfoIcon(dataStatus, size);
}

export function getTargetDisplayedId(target: Target<AbstractLocation> | null) {
  return target?.displayId ?? target?.id ?? appIntl().formatMessage({ id: 'target.unknown' });
}

export function getUniqueSensorShortHand(tracks: TrackLight[]) {
  return Array.from(new Set(tracks.map((track) => extendedSensorShortHand[track.sensorType])));
}

export function getUniqueSensorFamilies(tracks: TrackLight[]) {
  return Array.from(new Set(tracks.map((track) => sensorFamilyFromSensorType[track.sensorType])));
}
